// src/utils/getUserCategory.ts
//
// Kategorie střelce v profilu: "nečlen" / "člen" / "člen-junior" / "člen-žena".
// Jedna funkce místo tří kopií (UserProfilePage + 2× UpdateProfile, krok 5
// redesignu). Výsledek je shodný s dosavadní logikou UserProfilePage.
//
// POZOR – nezaměňovat s cenovou kategorií závodu (utils/pricing.ts,
// getPriceCategory): ta posuzuje členství podle ROKU ZÁVODU a žena v ní má
// přednost před juniorem. Tady se bere aktuální rok (nebo zadaný) a junior
// má přednost před ženou – tak profil kategorii ukazoval vždy.

import { useCallback, useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { reportError, reportWarning } from "./reportError";

/** Kategorie střelce v profilu (technické hodnoty shodné s dřívějším zobrazením). */
export type UserCategory = "nečlen" | "člen" | "člen-junior" | "člen-žena";

/** Čitelné popisky kategorií pro UI. */
export const USER_CATEGORY_LABELS: Record<UserCategory, string> = {
  "nečlen": "Nečlen",
  "člen": "Člen",
  "člen-junior": "Člen – junior",
  "člen-žena": "Člen – žena",
};

/** Pole dokumentu users/{uid}, ze kterých se kategorie počítá (DocumentData vyhovuje). */
export interface UserCategorySource {
  /** Rok narození; počítá se jen typ number (jinak je věk neznámý → nikdy junior). */
  birthYear?: unknown;
  /** Pohlaví ("žena", "ŽENA"…); porovnává se bez ohledu na velikost písmen. */
  gender?: unknown;
  /** Členství podle roku: { "2026": "ZAPLACENO", … } (velikost písmen nerozhoduje). */
  membership?: unknown;
}

export interface UserCategoryInput extends UserCategorySource {
  /**
   * Maximální věk juniora z defaults/categories (junior.maxAge).
   * null = nenačteno / nenastaveno → junior se nevyhodnocuje (stejně jako dřív).
   */
  juniorMaxAge: number | null;
  /** Referenční rok pro členství i věk. Výchozí aktuální rok. */
  year?: number;
}

/**
 * Kategorie střelce:
 * - bez zaplaceného členství v daném roce → "nečlen",
 * - věk (rok − rok narození) ≤ juniorMaxAge → "člen-junior",
 * - pohlaví žena → "člen-žena",
 * - jinak "člen".
 *
 * Oproti původním kopiím snese nečekané typy (členství nebo pohlaví, které
 * není řetězec) – dřív na nich volání toUpperCase() spadlo.
 */
export function getUserCategory(input: UserCategoryInput): UserCategory {
  const { birthYear, gender, membership, juniorMaxAge } = input;
  const year = input.year ?? new Date().getFullYear();

  const paid =
    membership !== null && typeof membership === "object"
      ? (membership as Record<string, unknown>)[year.toString()]
      : undefined;
  const isMember =
    typeof paid === "string" && paid.toUpperCase() === "ZAPLACENO";
  if (!isMember) return "nečlen";

  const age = typeof birthYear === "number" ? year - birthYear : null;
  if (age !== null && juniorMaxAge !== null && age <= juniorMaxAge) {
    return "člen-junior";
  }
  if (typeof gender === "string" && gender.toUpperCase() === "ŽENA") {
    return "člen-žena";
  }
  return "člen";
}

// ─────────────────────────────────────────────────────────────
//  Věková hranice juniora z defaults/categories
// ─────────────────────────────────────────────────────────────

/** Poslední úspěšně načtená hodnota; undefined = v této relaci ještě nenačteno. */
let cachedJuniorMaxAge: number | null | undefined;
/** Rozběhnuté čtení – souběžná volání sdílejí jeden požadavek. */
let pendingJuniorMaxAge: Promise<number | null> | null = null;

/**
 * Načte junior.maxAge z dokumentu defaults/categories – stejný zdroj, ze
 * kterého ho dosud četly UserProfilePage, UpdateProfile, UserDashboard
 * i PaymentRace. Vrací číslo, nebo null, když dokument/pole chybí nebo není
 * číslo. Při chybě čtení vrátí poslední hodnotu úspěšně načtenou v této
 * relaci (jen reportWarning – uživatel nic nepozná); bez ní chybu zapíše
 * (reportError) a propaguje (reject), ať obrazovka ví, že hranici nemá,
 * a nabídne „Zkusit znovu".
 */
export function fetchJuniorMaxAge(): Promise<number | null> {
  if (pendingJuniorMaxAge) return pendingJuniorMaxAge;
  pendingJuniorMaxAge = getDoc(doc(db, "defaults", "categories"))
    .then((snap) => {
      const raw = snap.exists() ? snap.data()?.junior?.maxAge : undefined;
      const value =
        typeof raw === "number" && Number.isFinite(raw) ? raw : null;
      cachedJuniorMaxAge = value;
      return value;
    })
    .catch((err: unknown) => {
      if (cachedJuniorMaxAge === undefined) {
        reportError("fetchJuniorMaxAge", err);
        throw err;
      }
      reportWarning("fetchJuniorMaxAge", err, { cached: cachedJuniorMaxAge });
      return cachedJuniorMaxAge;
    })
    .finally(() => {
      pendingJuniorMaxAge = null;
    });
  return pendingJuniorMaxAge;
}

export interface JuniorMaxAgeState {
  /** junior.maxAge; null = nenastaveno, ještě nenačteno nebo chyba čtení. */
  juniorMaxAge: number | null;
  /**
   * True až po úspěšném načtení (hned, když je hodnota z této relace
   * v cache). Při chybě bez cache false – hodnota pak není známá.
   */
  ready: boolean;
  /**
   * Chyba posledního čtení, když žádná hodnota není k dispozici; null = bez
   * chyby. Stav „načítá se" = !ready && error === null.
   */
  error: unknown;
  /** Zkusí hranici načíst znovu (tlačítko „Zkusit znovu"). */
  reload: () => void;
}

/**
 * Hook: věková hranice juniora pro obrazovky (obrazovky ji nemusí číst samy).
 * Startuje z hodnoty načtené dříve v relaci (bez bliknutí kategorie při
 * přechodu Profil → Úprava profilu) a při každém mountu ji načte znovu,
 * kdyby ji admin mezitím změnil. Selhání čtení bez hodnoty v cache skončí
 * jako ready false + error; reload čtení zopakuje.
 */
export function useJuniorMaxAge(): JuniorMaxAgeState {
  const [state, setState] = useState<Omit<JuniorMaxAgeState, "reload">>(
    () => ({
      juniorMaxAge: cachedJuniorMaxAge ?? null,
      ready: cachedJuniorMaxAge !== undefined,
      error: null,
    })
  );
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let alive = true;
    // Opakované čtení (reload) začíná bez chyby – obrazovka ukáže načítání.
    setState((s) => (s.error === null ? s : { ...s, error: null }));
    fetchJuniorMaxAge().then(
      (value) => {
        if (alive) setState({ juniorMaxAge: value, ready: true, error: null });
      },
      (err: unknown) => {
        if (alive) setState({ juniorMaxAge: null, ready: false, error: err });
      }
    );
    return () => {
      alive = false;
    };
  }, [reloadTick]);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  return { ...state, reload };
}

/**
 * Hook: kategorie přihlášeného střelce z jeho users dokumentu.
 * Vrací null, dokud není dokument nebo věková hranice juniora načtená
 * (obrazovka tak kategorii neukáže nejdřív špatně a pak správně) – a stejně
 * tak null, když čtení hranice selhalo bez hodnoty v cache (signatura
 * i chování pro volající zůstávají: null = kategorie není známá).
 * Po načtení hranice počítá stejně jako getUserCategory.
 */
export function useUserCategory(
  userDoc: UserCategorySource | null | undefined
): UserCategory | null {
  const { juniorMaxAge, ready, error } = useJuniorMaxAge();
  if (!userDoc) return null;
  // null jen během načítání; po chybě čtení defaults/categories se kategorie
  // spočítá bez juniorské hranice (jako dřív) – jinak by „Načítám…" viselo
  // navždy bez možnosti opakování.
  if (!ready && error == null) return null;
  return getUserCategory({
    birthYear: userDoc.birthYear,
    gender: userDoc.gender,
    membership: userDoc.membership,
    juniorMaxAge: ready ? juniorMaxAge : null,
  });
}

export default getUserCategory;
